import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { authApi, meApi } from '@/api/resources'
import { ApiError } from '@/api/client'
import type { CurrentUser } from '@/types/api'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<CurrentUser | null>(null)
  const loading = ref(false)
  /** Dokud neproběhne první ověření session, router nesmí rozhodovat o přesměrování. */
  const initialized = ref(false)

  const isAuthenticated = computed(() => user.value !== null)
  const isAdmin = computed(
    () => user.value?.roles.some((role) => role === 'ROLE_ADMIN' || role === 'ROLE_SUPER_ADMIN') ?? false,
  )

  function hasRole(role: string): boolean {
    return user.value?.roles.includes(role) ?? false
  }

  /**
   * Zjistí, jestli je uživatel přihlášený. Volá se při startu aplikace —
   * token je v httpOnly cookie, takže jinak se to z JS zjistit nedá.
   */
  async function loadProfile(): Promise<void> {
    loading.value = true
    try {
      user.value = await meApi.profile()
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        user.value = null
      } else {
        throw error
      }
    } finally {
      loading.value = false
      initialized.value = true
    }
  }

  async function login(email: string, password: string): Promise<void> {
    await authApi.login(email, password)
    // Login vrací jen základní profil; načteme plný včetně počtu notifikací.
    await loadProfile()
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout()
    } finally {
      user.value = null
    }
  }

  /** Vynucené odhlášení po vypršení session — bez volání API. */
  function clear(): void {
    user.value = null
    initialized.value = true
  }

  function setUnreadCount(count: number): void {
    if (user.value) {
      user.value = { ...user.value, unreadNotifications: count }
    }
  }

  return {
    user,
    loading,
    initialized,
    isAuthenticated,
    isAdmin,
    hasRole,
    loadProfile,
    login,
    logout,
    clear,
    setUnreadCount,
  }
})
